import { useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, FileDown } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { PORTRAIT_URL } from "@/data/content";

const ease = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { t, lang } = useLang();
  const pdfHref = `/downloads/Klaus_Baumgart_Portfolio_${lang === "de" ? "DE" : "EN"}.pdf`;
  const [imgOk, setImgOk] = useState(true);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const portraitY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative min-h-screen flex flex-col overflow-hidden hero-grid-bg">
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0">
        <img
          src="https://images.pexels.com/photos/6159262/pexels-photo-6159262.jpeg"
          alt=""
          className="w-full h-full object-cover opacity-20 grayscale"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/60 to-ink/30" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pt-28 lg:pt-32 pb-8 flex-1 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <MaskedLine delay={0.2} className="font-mono text-xs uppercase tracking-[0.3em] text-volt mb-6">
            {t.hero.overline}
          </MaskedLine>

          <h1 className="font-heading font-extrabold tracking-tighter leading-[0.9] text-paper text-4xl sm:text-5xl lg:text-6xl xl:text-[5.5rem]">
            <MaskedLine delay={0.4}>KLAUS</MaskedLine>
            <MaskedLine delay={0.55} className="text-outline">BAUMGART</MaskedLine>
          </h1>

          <MaskedLine delay={0.75} className="font-mono text-xs md:text-sm text-dim tracking-[0.15em] mt-6">
            {t.hero.role}
          </MaskedLine>

          <div data-testid="hero-tagline" className="mt-8 font-heading font-bold tracking-tight leading-tight text-2xl sm:text-3xl xl:text-[2.6rem] text-paper">
            <MaskedLine delay={0.9}>{t.hero.tagline[0]}</MaskedLine>
            <MaskedLine delay={1.0}>
              <span className="text-volt">{t.hero.tagline[1]}</span>
            </MaskedLine>
            <MaskedLine delay={1.1}>{t.hero.tagline[2]}</MaskedLine>
            <MaskedLine delay={1.2}>
              <span className="text-volt">{t.hero.tagline[3]}</span>
            </MaskedLine>
          </div>

          <MaskedLine delay={1.3} className="font-mono text-sm text-dim mt-6 tracking-wide">
            {t.hero.sub}
          </MaskedLine>

          <MaskedLine delay={1.45}>
            <a
              href={pdfHref}
              download
              data-testid="hero-download-cv-button"
              className="inline-flex items-center gap-3 mt-8 border border-volt text-volt font-mono text-xs uppercase tracking-[0.25em] px-6 py-3.5 hover:bg-volt hover:text-ink transition-colors duration-300 group"
            >
              <FileDown size={15} className="group-hover:translate-y-0.5 transition-transform" />
              {t.hero.downloadCv}
            </a>
          </MaskedLine>
        </div>

        <motion.div
          className="lg:col-span-5"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.1, ease }}
        >
          <motion.div style={{ y: portraitY }} data-testid="hero-portrait" className="relative max-w-[260px] sm:max-w-xs xl:max-w-sm mx-auto lg:ml-auto lg:mr-4">
            <div className="absolute inset-0 border border-volt/50 translate-x-4 translate-y-4 pointer-events-none" />
            <div className="relative aspect-[4/5] bg-graphite border border-line overflow-hidden group">
              {imgOk ? (
                <img
                  src={PORTRAIT_URL}
                  alt="Klaus Baumgart"
                  onError={() => setImgOk(false)}
                  className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-4">
                  <span className="font-heading font-extrabold text-8xl text-outline-dim">KB.</span>
                  <span className="font-mono text-[10px] tracking-[0.3em] text-dim uppercase">{t.hero.portraitNote}</span>
                </div>
              )}
              <div className="absolute bottom-0 left-0 right-0 bg-ink/80 backdrop-blur-sm border-t border-line px-4 py-3 flex justify-between">
                <span className="font-mono text-[10px] tracking-[0.25em] text-paper">KLAUS BAUMGART</span>
                <span className="font-mono text-[10px] tracking-[0.25em] text-volt">{t.hero.portraitTag}</span>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="relative z-10 max-w-7xl mx-auto w-full px-6 lg:px-10 pb-10 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-dim"
        data-testid="hero-scroll-indicator"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={14} className="text-volt" />
        </motion.span>
        {t.hero.scroll}
      </motion.div>
    </section>
  );
};
